'use client';

import { useEffect, useState } from 'react';

import { useIsAppUser } from '../hooks/useIsAppUser';
import { postToNative } from './bridge';

/** Mirrors PopshopRN's `kAppPermissionsGrantedKey`. */
const APP_PERMISSIONS_GRANTED_KEY = 'appPermissionsGranted';

/**
 * Read whether the native OS media permissions (camera/mic/photos) are already
 * granted. The shell injects `?appPermissionsGranted=` on the entry URL; we
 * persist it (the param is dropped on later navigations), matching PopshopRN.
 */
function readGranted(): boolean {
  if (typeof window === 'undefined') return false;
  // Same double-`?` sanitize the shell's other params get.
  const params = new URLSearchParams(
    window.location.search.replaceAll('?', '&'),
  );
  const fromUrl = params.get(APP_PERMISSIONS_GRANTED_KEY);
  if (fromUrl != null) {
    try {
      localStorage.setItem(APP_PERMISSIONS_GRANTED_KEY, fromUrl);
    } catch {
      // localStorage unavailable
    }
    return fromUrl === 'true';
  }
  try {
    return localStorage.getItem(APP_PERMISSIONS_GRANTED_KEY) === 'true';
  } catch {
    return false;
  }
}

/**
 * Native media-permission gate for file/camera pickers.
 *
 * `ensureNativePermission()` MUST be called synchronously from the picker's
 * click handler and then followed IMMEDIATELY by `input.click()` — it is
 * deliberately fire-and-forget (never `await` it), because awaiting would drop
 * the browser user-gesture and the file dialog would silently fail to open. It
 * asks the shell to request OS permissions (camera/mic/photos) only when they
 * aren't already granted; the shell surfaces the OS prompt (or Settings, if
 * previously denied) out of band and dispatches `appPermissionsResult`, which
 * we persist so we stop re-asking. On the plain web it is a no-op.
 */
export function useAppMediaPermission(): {
  granted: boolean;
  ensureNativePermission: () => void;
} {
  const isAppUser = useIsAppUser();
  const [granted, setGranted] = useState<boolean>(() => readGranted());

  useEffect(() => {
    if (!isAppUser) return;
    const onResult = (event: Event) => {
      const ok = !!(event as CustomEvent<{ granted: boolean }>).detail?.granted;
      setGranted(ok);
      try {
        localStorage.setItem(APP_PERMISSIONS_GRANTED_KEY, String(ok));
      } catch {
        // localStorage unavailable
      }
    };
    window.addEventListener('appPermissionsResult', onResult);
    return () => window.removeEventListener('appPermissionsResult', onResult);
  }, [isAppUser]);

  const ensureNativePermission = () => {
    if (isAppUser && !granted) {
      postToNative({ type: 'request-app-permissions' });
    }
  };

  return { granted, ensureNativePermission };
}
